var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Cartesian3, Cartographic, Ellipsoid, IntersectionTests, Plane, Ray, SceneTransforms, ScreenSpaceEventType, } from "cesium";
import { ConstructionState } from "../../site/trench/earthbrain-trench";
import { CesiumPointSelectorType, } from "../../cesium-utils/point-selector/point-selector";
import { EarthBrainUtils } from "../../utilities/earthbrainutility";
import { ViewerCamera } from "../../viewers/viewer-camera";
import { AddPointToFlatGroundAtIndexCommand, RemovePointFromFlatGroundCommand, UpdateAllControlPointHeightCommand, UpdateFlatGroundPointPositionCommand, } from "./flatground-command";
function isEditable() {
    const retVal = (target, key, descriptor) => {
        const original = descriptor.value;
        descriptor.value = function (...args) {
            const flatGroundEditHandler = this;
            if (flatGroundEditHandler.flatGroundUnderEdit) {
                return original.apply(this, args);
            }
            else {
                console.error(`Call start on FlatGroundEditHandler before calling ${String(key)}`);
            }
        };
        return descriptor;
    };
    return retVal;
}
export class FlatGroundEditHandler {
    _callback;
    _flatGroundUnderEdit;
    _viewer;
    _preview;
    _commandManager;
    _previewPlane;
    _handler;
    _cesiumPicker;
    _cesiumPointSelector;
    _site;
    _cesiumViewer;
    _viewerCamera;
    _deltaValue;
    _isFlatGroundToUniformDrag;
    _isFlatGroundPointToVerticalDrag;
    get flatGroundUnderEdit() {
        return this._flatGroundUnderEdit;
    }
    get isFlatGroundToUniformDrag() {
        return this._isFlatGroundToUniformDrag;
    }
    get isFlatGroundPointToVerticalDrag() {
        return this._isFlatGroundPointToVerticalDrag;
    }
    constructor(args, cesiumViewer) {
        this._viewer = args.viewer;
        this._preview = args.preview;
        this._commandManager = args.commandManger;
        this._handler = args.handler;
        this._cesiumPicker = args.cesiumPicker;
        this._callback = {};
        this._cesiumViewer = cesiumViewer;
        this._viewerCamera = ViewerCamera.getInstance(this._viewer);
        this._site = args.site;
        this._deltaValue = new Cartesian3(0, 0, 0);
        this._isFlatGroundPointToVerticalDrag = false;
        this._isFlatGroundToUniformDrag = false;
        this._cesiumPointSelector =
            args.cesiumPointSelectorFactory.getPointSelector(CesiumPointSelectorType.PriorityBased);
    }
    start(flatGround, callback) {
        this._callback = callback;
        this._flatGroundUnderEdit = flatGround;
        this._flatGroundUnderEdit.constructionState = ConstructionState.UNDER_EDIT;
        this._commandManager.clear();
        this._isFlatGroundPointToVerticalDrag = false;
        this._isFlatGroundToUniformDrag = false;
        this._flatGroundUnderEdit.fitToFlatGround();
        this.setFlatGroundPointToHorizontalDrag();
        this._flatGroundUnderEdit.updateOutline();
        this._callback.onStart && this._callback.onStart();
    }
    end() {
        this._commandManager.clear();
        this._handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this._resetDragFeature();
        this._preview.setSphereVisibility(false);
        this._preview.setLineVisibility(false);
        this._preview.setVerticalLineEntityVisibility(false);
        this._preview.setLinePosition([]);
        this._flatGroundUnderEdit.disableForEdit();
        this._flatGroundUnderEdit.constructionState = ConstructionState.READY;
        this._flatGroundUnderEdit = undefined;
        this._isFlatGroundPointToVerticalDrag = false;
        this._isFlatGroundToUniformDrag = false;
    }
    cancel() {
        //To Do All changes should be undone
        this.end();
    }
    removePointFromFlatGround(flatGroundPointID) {
        if (this._flatGroundUnderEdit.GroundPointsCount > 3) {
            this._preview.getLengthOfArrowMap() > 0 &&
                this._preview.removeArrowEntity(flatGroundPointID);
            this._commandManager.action(new RemovePointFromFlatGroundCommand(this._flatGroundUnderEdit, flatGroundPointID, this._callback.onProgress), this._cesiumViewer);
        }
        else {
            console.warn("Can't remove point as minimum three points are required for flat ground.");
        }
    }
    async updateCtrlPointCordinateOnFlatGround(flatGroundPointID, point) {
        const arr = await this._site.convertPointsToLocalSpace([point]);
        const localPos = arr[0];
        const windowCoordinates = SceneTransforms.worldToWindowCoordinates(this._viewer.scene, point);
        const pickedInfo = this._cesiumPicker.getPickablePickedInfo(windowCoordinates);
        if (pickedInfo?.structureInfo?.id === this._flatGroundUnderEdit.id) {
            pickedInfo.structureInfo = undefined;
        }
        this._commandManager.action(new UpdateFlatGroundPointPositionCommand(this._flatGroundUnderEdit, { position: point, info: pickedInfo, localPosition: localPos }, flatGroundPointID, undefined, this._callback.onProgress), this._cesiumViewer);
    }
    addFlatGroundControlPointOnOutline(posOnOutline) {
        const { segmentIndex } = EarthBrainUtils.getIndexForProbableSegmentAndProjectedPoint(posOnOutline, this._flatGroundUnderEdit.points);
        const index = segmentIndex + 1;
        this._commandManager.action(new AddPointToFlatGroundAtIndexCommand(this._flatGroundUnderEdit, index, posOnOutline, this._callback.onProgress), this._cesiumViewer);
        // this._viewer.scene.requestRender();
    }
    /**
     * Handles creation, mouse events and disposal of the arrow entity
     * @param status controls the creation of flatground ceneter arrow entity
     * @param flatGroundID
     */
    setFlatGroundToUniformDrag() {
        this._resetDragFeature();
        this._preview.previewCenterArrowRenderStatus = true;
        this._isFlatGroundToUniformDrag = true;
        this._preview.arrowPreviewForFlatGroundCenter(this._flatGroundUnderEdit, 10, 5);
    }
    setFlatGroundPointToVerticalDrag() {
        this._resetDragFeature();
        this._preview.previewArrowRenderStatus = true;
        this._isFlatGroundPointToVerticalDrag = true;
        this._preview.arrowPreviewForFlatArea(this._flatGroundUnderEdit, 10, 5);
    }
    setFlatGroundPointToHorizontalDrag() {
        this._resetDragFeature();
    }
    async setFlatGroundPointsUniformHeight(id, height) {
        const flatGround = this._site.getFlatGround(id);
        flatGround.setUniformPointsHeight(height);
        this._site.structureManager.reComputeConnectionNodesConnectedToChildStructure(flatGround.id);
        await flatGround.computeGeometry();
        const structure = this._site.structureManager.getStructure(flatGround.id);
        structure?.updateConnectionOnAddUpdatePoint();
        this._site.structureManager.reComputeChildrenGeometry(flatGround.id);
        if (flatGround.isVisible()) {
            flatGround.updateSpheresPositionAndLabel();
            flatGround.updateOutline();
            this._preview.arrowPreviewForFlatArea(flatGround, 10, 5);
            this._preview.arrowPreviewForFlatGroundCenter(flatGround, 10, 5);
        }
        this._callback.onProgress();
    }
    /**
     * This function will return flat ground ID, flat ground point ID, flat ground outline ID & Cartesian3 pick position
     * @param x screen coordinate
     * @param y screen coordinate
     * @throws {Error}
     */
    async pickFlatGroundEntities(mousePos) {
        let result = {
            flatGroundId: undefined,
            pointId: undefined,
            position: undefined,
            type: undefined,
        };
        const pickModel = this._cesiumPicker.getPickablePickedInfo(mousePos);
        if (pickModel) {
            result = {
                flatGroundId: pickModel.owner.id,
                pointId: pickModel.entity.id,
                position: undefined,
                type: pickModel.pickableType,
            };
            const ray = this._cesiumPicker.getPickRay(mousePos);
            if (!ray) {
                return result;
            }
            const nextPoint = Cartesian3.add(ray.origin, ray.direction, new Cartesian3());
            const localPoints = await this._site.convertPointsToLocalSpace([
                ray.origin,
                nextPoint,
            ]);
            const localDir = Cartesian3.subtract(localPoints[1], localPoints[0], new Cartesian3());
            Cartesian3.normalize(localDir, localDir);
            const localRay = new Ray(localPoints[0], localDir);
            const clickedLocalPointOnFlatGround = this._flatGroundUnderEdit.rayCast(localRay);
            if (!clickedLocalPointOnFlatGround) {
                return result;
            }
            const clickedWgs84PointOnFlatGround = await this._site.convertPointsToWGS84([clickedLocalPointOnFlatGround]);
            result.position = clickedWgs84PointOnFlatGround[0];
        }
        return result;
    }
    //Private methods
    _resetDragFeature() {
        this._preview.previewArrowRenderStatus = false;
        this._preview.previewCenterArrowRenderStatus = false;
        this._isFlatGroundPointToVerticalDrag = false;
        this._isFlatGroundToUniformDrag = false;
        this._preview.disposeFlatGroundCenterArrow();
        this._preview.disposeArrowPreviewForFlatArea();
    }
    async _flatGroundPointPosUpdate(clickedPointID, clickEvent) {
        const cartesian = this._cesiumPicker.getPointAtPlaneOnMousePos(clickEvent.position, this._previewPlane);
        if (cartesian) {
            const arr = await this._site.convertPointsToLocalSpace([cartesian]);
            const localPos = arr[0];
            const pickedInfo = this._cesiumPicker.getPickablePickedInfo(clickEvent.position);
            if (pickedInfo?.structureInfo?.id === this._flatGroundUnderEdit.id) {
                pickedInfo.structureInfo = undefined;
            }
            this._commandManager.action(new UpdateFlatGroundPointPositionCommand(this._flatGroundUnderEdit, { position: cartesian, info: pickedInfo, localPosition: localPos }, clickedPointID, undefined, this._callback.onProgress), this._cesiumViewer);
        }
        this._handler.removeInputAction(ScreenSpaceEventType.LEFT_UP);
        this._handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        // Now need to enable the camera
        this._viewer.scene.screenSpaceCameraController.enableRotate = true;
        this._preview.setSphereVisibility(false);
    }
    _flatGroundPointMove(event) {
        const cartesian = this._cesiumPicker.getPointAtPlaneOnMousePos(event.endPosition, this._previewPlane);
        if (cartesian) {
            this._preview.setSpherePosition(cartesian);
        }
    }
    async pickControlPoint(click) {
        const pickedFlatGroundPoint = this._cesiumPicker.getRenderedEntity(click.position);
        if (pickedFlatGroundPoint &&
            this._flatGroundUnderEdit.containsPointWithID(pickedFlatGroundPoint.id)) {
            // Finding which Flat Ground Point has been clicked on the basis of clicked point id
            // Storing the ID of the control point
            const clickedPointID = pickedFlatGroundPoint.id;
            // Enable the preview points
            this._preview.setSphereVisibility(true);
            const pos = await this._cesiumPointSelector.getPointOnMousePos(click.position);
            if (!pos) {
                return;
            }
            this._preview.setSpherePosition(pos);
            this._preview.createVerticalLineEntityOnSphere();
            // create plane parallel to surface which will be used to pick the point to move the preview sphere
            const planeNormal = Ellipsoid.WGS84.geodeticSurfaceNormal(pickedFlatGroundPoint._position._value);
            this._previewPlane = Plane.fromPointNormal(pickedFlatGroundPoint._position._value, planeNormal);
            this._handler.setInputAction(this._flatGroundPointPosUpdate.bind(this, clickedPointID), ScreenSpaceEventType.LEFT_UP);
            this._handler.setInputAction(this._flatGroundPointMove.bind(this), ScreenSpaceEventType.MOUSE_MOVE);
            // Now need to disable the camera
            this._viewer.scene.screenSpaceCameraController.enableRotate = false;
        }
    }
    /**
     *
     * @param selectedPreviewID selected entity id/arrow entity
     * @param plane for ray intersection
     * @param arrowStartPos start position of arrow entity
     * @param flatGround flat ground
     * @param clickedEvent mouse up event
     */
    _flatGroundCenterArrowMouseLeftUp(selectedPreviewID, plane, arrowStartPos, clickedEvent) {
        const ray = this._cesiumPicker.getPickRay(clickedEvent.position);
        const intersectedNewPos = IntersectionTests.rayPlane(ray, plane);
        const updatedPosition = this._arrowPreviewUpdate(selectedPreviewID, arrowStartPos, intersectedNewPos);
        const updatedPosCarto = Cartographic.fromCartesian(updatedPosition);
        const arrowStartPosCarto = Cartographic.fromCartesian(arrowStartPos);
        const offsetForAllFlatGroundPoints = updatedPosCarto.height - arrowStartPosCarto.height;
        this._commandManager.action(new UpdateAllControlPointHeightCommand(this._flatGroundUnderEdit, offsetForAllFlatGroundPoints, this._callback.onProgress), this._cesiumViewer);
        this._preview.setSphereVisibility(false);
        this._handler.removeInputAction(ScreenSpaceEventType.LEFT_UP);
        this._handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        // Now need to enable the camera
        this._viewer.scene.screenSpaceCameraController.enableRotate = true;
    }
    /**
     * Triggers when left down operation is performed on the flatground center arrow entity.
     * @param flatGround
     * @param click mouse down
     */
    dragFlatGroundUniformlyOnLeftDown(click) {
        const pickedObj = this._viewer.scene.pick(click.position);
        const flatGroundCenterArrow = this._preview.getPreviewFlatGroundCenterArrow();
        const flatGroundCenterArrowPosition = this._preview.getPreviewFlatGroundCenterArrowPosition();
        if (flatGroundCenterArrow &&
            pickedObj &&
            pickedObj.id?.id === flatGroundCenterArrow.id) {
            // Enable the preview points
            this._preview.setSphereVisibility(true);
            // Now need to disable the camera
            this._viewerCamera.setEnableRotate(false);
            if (flatGroundCenterArrowPosition) {
                // - Calculate geodetic direction (A) this point
                const surfaceNormal = this._viewer.scene.globe.ellipsoid.geodeticSurfaceNormal(flatGroundCenterArrowPosition);
                // - Find the camera direction (from point to camera pos) (B)
                const ray = new Ray(flatGroundCenterArrowPosition, this._viewerCamera.rawCamera.direction);
                // - Calculate side direction for the plane. Cross A with B get C
                const sideDirection = Cartesian3.cross(surfaceNormal, ray.direction, new Cartesian3(0, 0, 0));
                // - Cross C with A to get the normal of the plane.
                const planeNormal = Cartesian3.cross(sideDirection, surfaceNormal, new Cartesian3(0, 0, 0));
                Cartesian3.normalize(planeNormal, planeNormal);
                // - Calculating the PLANE
                const plane = Plane.fromPointNormal(flatGroundCenterArrowPosition, planeNormal);
                // Calculating Intersection Point
                const startPos = IntersectionTests.rayPlane(ray, plane);
                // Delta value calculation
                const deltaRay = this._cesiumPicker.getPickRay(click.position);
                const deltaPos = IntersectionTests.rayPlane(deltaRay, plane);
                Cartesian3.subtract(deltaPos, startPos, this._deltaValue);
                if (startPos) {
                    this._handler.setInputAction(this._flatGroundCenterArrowMouseLeftUp.bind(this, flatGroundCenterArrow.id, plane, flatGroundCenterArrowPosition), ScreenSpaceEventType.LEFT_UP);
                    this._handler.setInputAction(this._flatGroundCenterArrowMouseMove.bind(this, flatGroundCenterArrow.id, plane, startPos), ScreenSpaceEventType.MOUSE_MOVE);
                    this._viewerCamera.setEnableRotate(false);
                }
            }
        }
    }
    _arrowPreviewUpdate(selectedPreviewID, startPos, intersectedNewPos) {
        const newPos = new Cartesian3(0, 0, 0);
        Cartesian3.subtract(intersectedNewPos, this._deltaValue, newPos);
        const startPosCarto = Cartographic.fromCartesian(startPos);
        const newPosCarto = Cartographic.fromCartesian(newPos);
        const movedPosCarto = Cartographic.fromRadians(startPosCarto.longitude, startPosCarto.latitude, newPosCarto.height);
        const movedPosCartoAddedArrowHeight = Cartographic.fromRadians(startPosCarto.longitude, startPosCarto.latitude, newPosCarto.height + 5);
        const movedPosCarte = Cartographic.toCartesian(movedPosCarto);
        const movedPosCarteAddedArrowHeight = Cartographic.toCartesian(movedPosCartoAddedArrowHeight);
        this._preview.setArrowPosition(selectedPreviewID, [
            movedPosCarte,
            movedPosCarteAddedArrowHeight,
        ]);
        return movedPosCarte;
    }
    pickArrowEntityForVerDrag(click) {
        const pickedObj = this._viewer.scene.pick(click.position);
        if (pickedObj && pickedObj.id) {
            // Finding which Flat Ground Point's ARROW has been clicked on the basis of clicked point id
            let selectedPreviewEntity = this._preview.getArrowEntityFromArrowId(pickedObj.id.id);
            selectedPreviewEntity = selectedPreviewEntity
                ? selectedPreviewEntity
                : this._preview.getArrowEntityFromSphereId(pickedObj.id.id);
            // const selectedPreviewID = selectedPreviewEntity ? selectedPreviewEntity.id : undefined;
            if (selectedPreviewEntity && selectedPreviewEntity.id) {
                // Steps for calculating the PLANE:
                // - Point should be arrow start point
                let arrowStartPoint = new Cartesian3(0, 0, 0);
                arrowStartPoint = this._preview.getArrowEntityPosition(selectedPreviewEntity.id);
                if (arrowStartPoint) {
                    // - Calculate geodetic direction (A) this point
                    const surfaceNormal = this._viewer.scene.globe.ellipsoid.geodeticSurfaceNormal(arrowStartPoint);
                    // - Find the camera direction (from point to camera pos) (B)
                    const ray = new Ray(arrowStartPoint, this._viewerCamera.rawCamera.direction);
                    // - Calculate side direction for the plane. Cross A with B get C
                    const sideDirection = new Cartesian3(0, 0, 0);
                    Cartesian3.cross(surfaceNormal, ray.direction, sideDirection);
                    // - Cross C with A to get the normal of the plane.
                    const planeNormal = new Cartesian3(0, 0, 0);
                    Cartesian3.cross(sideDirection, surfaceNormal, planeNormal);
                    Cartesian3.normalize(planeNormal, planeNormal);
                    // - Calculating the PLANE
                    const plane = Plane.fromPointNormal(arrowStartPoint, planeNormal);
                    // Calculating Intersection Point
                    const startPos = IntersectionTests.rayPlane(ray, plane);
                    // Delta value calculation
                    const deltaRay = this._cesiumPicker.getPickRay(click.position);
                    const deltaPos = IntersectionTests.rayPlane(deltaRay, plane);
                    Cartesian3.subtract(deltaPos, startPos, this._deltaValue);
                    if (startPos) {
                        // call a move handler
                        this._handler.setInputAction(this._pointArrowPosUpdate.bind(this, selectedPreviewEntity.id, plane, startPos, selectedPreviewEntity), ScreenSpaceEventType.LEFT_UP);
                        this._handler.setInputAction(this._pointArrowMove.bind(this, selectedPreviewEntity.id, plane, startPos), ScreenSpaceEventType.MOUSE_MOVE);
                        this._viewerCamera.setEnableRotate(false);
                    }
                }
            }
        }
    }
    async _pointArrowPosUpdate(selectedPreviewID, plane, startPos, selectedPreviewEntity, clickedEvent) {
        const index = this._preview.getSelectedArrowEntityIndex(selectedPreviewID);
        const flatGroundPointID = this._flatGroundUnderEdit.getFlatGroundPointID(index);
        const rayUpdate = this._cesiumPicker.getPickRay(clickedEvent.position);
        const intersectedNewPos = IntersectionTests.rayPlane(rayUpdate, plane);
        if (intersectedNewPos) {
            const updatedPos = this._arrowPreviewUpdate(selectedPreviewID, startPos, intersectedNewPos);
            const arr = await this._site.convertPointsToLocalSpace([updatedPos]);
            const localPos = arr[0];
            const pickedInfo = this._cesiumPicker.getPickablePickedInfo(clickedEvent.position);
            this._commandManager.action(new UpdateFlatGroundPointPositionCommand(this._flatGroundUnderEdit, { position: updatedPos, info: pickedInfo, localPosition: localPos }, flatGroundPointID, selectedPreviewEntity, this._callback.onProgress), this._cesiumViewer);
        }
        this._preview.setSphereVisibility(false);
        this._handler.removeInputAction(ScreenSpaceEventType.LEFT_UP);
        this._handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        // Now need to enable the camera
        this._viewerCamera.setEnableRotate(true);
    }
    /**
     * Take in plane, start position of object and mouseMoveEvent, calculate the intersecting mouse position with plane when we move the mouse
     * then find the difference between startPos and intersected mouse move position with the plane
     * @param plane
     * @param startPos
     * @param mouseMoveEvent
     */
    _pointArrowMove(selectedPreviewID, plane, startPos, mouseMoveEvent) {
        const updatedRay = this._cesiumPicker.getPickRay(mouseMoveEvent.endPosition);
        const newIntersectedPoint = IntersectionTests.rayPlane(updatedRay, plane);
        if (newIntersectedPoint) {
            const updatedPos = this._arrowPreviewUpdate(selectedPreviewID, startPos, newIntersectedPoint);
            this._preview.setSphereVisibility(true);
            this._preview.setSpherePosition(updatedPos);
            this._viewer.scene.requestRender();
        }
    }
    /**
     *
     * @param selectedPreviewID selected entity id/arrow entity
     * @param plane for ray intersection
     * @param startPos start position of arrow
     * @param mouseMoveEvent
     */
    _flatGroundCenterArrowMouseMove(selectedPreviewID, plane, startPos, mouseMoveEvent) {
        const ray = this._cesiumPicker.getPickRay(mouseMoveEvent.endPosition);
        const newIntersectedPoint = IntersectionTests.rayPlane(ray, plane);
        if (newIntersectedPoint) {
            const updatedPos = this._arrowPreviewUpdate(selectedPreviewID, startPos, newIntersectedPoint);
            this._preview.setSphereVisibility(true);
            this._preview.setSpherePosition(updatedPos);
            this._viewer.scene.requestRender();
        }
    }
}
__decorate([
    isEditable()
], FlatGroundEditHandler.prototype, "end", null);
__decorate([
    isEditable()
], FlatGroundEditHandler.prototype, "cancel", null);
__decorate([
    isEditable()
], FlatGroundEditHandler.prototype, "removePointFromFlatGround", null);
__decorate([
    isEditable()
], FlatGroundEditHandler.prototype, "updateCtrlPointCordinateOnFlatGround", null);
__decorate([
    isEditable()
], FlatGroundEditHandler.prototype, "addFlatGroundControlPointOnOutline", null);
__decorate([
    isEditable()
], FlatGroundEditHandler.prototype, "setFlatGroundToUniformDrag", null);
__decorate([
    isEditable()
], FlatGroundEditHandler.prototype, "setFlatGroundPointToVerticalDrag", null);
__decorate([
    isEditable()
], FlatGroundEditHandler.prototype, "setFlatGroundPointToHorizontalDrag", null);
__decorate([
    isEditable()
], FlatGroundEditHandler.prototype, "setFlatGroundPointsUniformHeight", null);
//# sourceMappingURL=flatground-edit-handler.js.map